import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { authSelectors } from '../store/selectors/auth';
import {
  certificates as fetchCertificates,
  downloadCertificate,
  issueCertificate,
} from '../api/certificates';

/**
 * Стан сертифікатів для сторінки курсів.
 *
 * ❗Контекст, а не Redux і не проп-дрилінг: дані потрібні кнопці в рядку
 * таблиці й у картці (це 3-4 рівні вкладеності від сторінки), але більше
 * ніде в кабінеті. Класти їх у глобальний стор означало б тримати там те,
 * що все одно перезапитується при кожному вході — умови змінюються після
 * кожного заняття.
 */
export const CertificatesContext = createContext({
  byProgram: {},
  openProgram: null,
  setOpenProgram: () => {},
  issue: () => {},
  download: () => {},
  busy: false,
  error: null,
  notice: null,
  clearMessages: () => {},
});

/** Зберігає blob як файл — PDF приходить тілом, прямого URL до нього немає. */
const saveFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const errorMessage = (err, fallback) => err?.response?.data?.info?.message || fallback;

/** Імʼя файлу для студента: курс + номер документа. */
const fileNameFor = (certificate) =>
  [certificate?.course_title, certificate?.number].filter(Boolean).join(' ') || 'certificate';

export const CertificatesProvider = ({ children }) => {
  const auth = useSelector(authSelectors);
  const token = auth?.token;

  const [items, setItems] = useState([]);
  const [openProgram, setOpenProgram] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const reload = useCallback(() => {
    if (!token) {
      return Promise.resolve();
    }

    return fetchCertificates(token)
      .then((res) => {
        if (res.data?.info?.status) {
          const list = res.data.data || [];

          setItems(list);
          // eslint-disable-next-line no-console
          console.info(
            '[certificates] отримано програм:', list.length,
            '| program_id:', list.map((item) => item.program_id)
          );

          return;
        }

        setItems([]);
        // eslint-disable-next-line no-console
        console.error('[certificates] бекенд відповів без status:', res.data);
      })
      .catch((err) => {
        setItems([]);

        // ❗Сторінку не валимо — сертифікати не головне на екрані курсів, кнопки
        // просто не зʼявляться. Але мовчати не можна: без статусу в консолі
        // «кнопок немає» невідрізниме від «умови не виконані».
        // eslint-disable-next-line no-console
        console.error(
          '[certificates] не вдалось отримати список.',
          'HTTP:', err?.response?.status ?? 'без відповіді',
          '| тіло:', err?.response?.data ?? err?.message
        );
      });
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  const byProgram = useMemo(
    () => items.reduce((acc, item) => ({ ...acc, [item.program_id]: item }), {}),
    [items]
  );

  const download = useCallback(
    (certificate) => {
      setBusy(true);
      setError(null);
      setNotice(null);

      return downloadCertificate({ token, certificateId: certificate.id })
        .then((res) => {
          saveFile(res.data, `${fileNameFor(certificate)}.pdf`);
          setNotice('Сертифікат успішно завантажено.');
        })
        .catch((err) => setError(errorMessage(err, 'Не вдалось завантажити файл. Спробуйте пізніше.')))
        .finally(() => setBusy(false));
    },
    [token]
  );

  const issue = useCallback(
    (programId) => {
      setBusy(true);
      setError(null);
      setNotice(null);

      return issueCertificate({ token, programId })
        .then((res) => {
          if (!res.data?.info?.status) {
            return null;
          }

          const certificateId = res.data.data.certificate_id;
          // created=false → CRM віддала вже наявний документ, нічого не робила.
          const created = res.data.data.created !== false;

          // Спершу перезапит, потім завантаження: після видачі змінюється стан
          // картки (зʼявляється номер), і саме звідти беремо імʼя файлу —
          // з тіла PDF його не дістати.
          return fetchCertificates(token).then((fresh) => {
            const list = fresh.data?.info?.status ? fresh.data.data || [] : [];

            setItems(list);

            const found = list
              .find((item) => item.program_id === programId)
              ?.certificates
              ?.find((certificate) => certificate.id === certificateId);

            return downloadCertificate({ token, certificateId }).then((file) => {
              saveFile(file.data, `${fileNameFor(found)}.pdf`);
              setNotice(
                created
                  ? 'Сертифікат успішно згенеровано і завантажено.'
                  : 'Сертифікат успішно завантажено.'
              );
            });
          });
        })
        .catch((err) => setError(errorMessage(err, 'Не вдалось отримати сертифікат. Спробуйте пізніше.')))
        .finally(() => setBusy(false));
    },
    [token]
  );

  const value = useMemo(
    () => ({
      byProgram,
      openProgram,
      setOpenProgram,
      issue,
      download,
      busy,
      error,
      notice,
      clearMessages: () => {
        setError(null);
        setNotice(null);
      },
    }),
    [byProgram, openProgram, issue, download, busy, error, notice]
  );

  return <CertificatesContext.Provider value={value}>{children}</CertificatesContext.Provider>;
};

CertificatesProvider.propTypes = {
  children: PropTypes.node,
};
